import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import config from "config";

export const axiosInstance = axios.create({
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.PUBLIC_API_URL,
});

export type Category = {
  type: string;
  subtype: string;
  subtypeDisplayName: string;
  detailType: string;
  detailTypeDisplayName: string;
  detailTypeDescription: string;
};

type GroupedCategory = {
  type: string;
  categories: Omit<Category, "type">[];
};

type RequestState = "NONE" | "LOADING" | "READY" | "ERROR";

export const useCategories = (search?: string) => {
  const [categories, setCategories] = useState<Category[] | undefined>();
  const [requestState, setRequestState] = useState<RequestState>("NONE");

  useEffect(() => {
    if (requestState === "NONE") {
      setRequestState("LOADING");
      axiosInstance
        .get<Category[]>("/metadata/accounts/categories")
        .then((response) => {
          setCategories(response.data);
          setRequestState("READY");
        })
        .catch(() => {
          setRequestState("ERROR");
        });
    }
  }, [categories, requestState]);

  const categoriesGrouped = useMemo(() => {
    const searchValue = search?.toLowerCase();
    const containsSearch = (category: Category) => {
      if (!searchValue) return true;
      return (
        category.type.toLowerCase().includes(searchValue) ||
        category.subtypeDisplayName.toLowerCase().includes(searchValue) ||
        category.detailTypeDisplayName.toLowerCase().includes(searchValue)
      );
    };

    return categories?.reduce<GroupedCategory[]>((grouped, category) => {
      const existing = grouped.find((g) => g.type === category.type);
      if (existing) {
        if (containsSearch(category)) {
          existing.categories.push(category);
        }
      } else {
        grouped.push({
          type: category.type,
          categories: containsSearch(category) ? [category] : [],
        });
      }
      return grouped;
    }, []);
  }, [categories, search]);

  return {
    categories,
    categoriesGrouped,
    error: requestState === "ERROR",
    loading: requestState === "LOADING",
  };
};
